import type {
	InvestmentSummary,
	ReviewTicker,
	UserInstrumentClassificationDto,
	UserInstrumentDto,
} from "@eu.mdotm.api/sphere";
import { Card } from "$root/components/EvolvedPrint/components/Card";
import {
	TinyTableDataCell,
	tinyTableDataCellFontSize,
	TinyTableHeadCell,
} from "$root/components/EvolvedPrint/components/table/tiny-table";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import { InfoDelta } from "$root/components/InfoDelta";
import { bankersRound, formatNumberWithBankersRounding } from "$root/localization/formatters";
import type {
	CompositionColumnPreference,
	ReportTemplateItemMap,
	ReportTemplateVariant,
} from "$root/functional-areas/reports/report-latest";
import { type TableColumn, BaseTable } from "@mdotm/shadcn/legacy/components";
import { useMemo } from "react";
import { useReportTranslation } from "../translation";
import { coalesce } from "$root/utils";
import { unreachable } from "@mdotm/utils/runtime";
import { TagCell } from "$root/ui-lib/table/custom-column/tags/tag-views";
import { MarkdownRenderer } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";

type MixedInvestmentEntry = Omit<ReviewTicker, "description"> & UserInstrumentDto;

const tableWidth = 560;
type ColumnMetadata<T> = {
	id: string;
	label: string;
	meta: T;
	hidden: boolean;
};
type ReportCompositionMetadata = ColumnMetadata<{
	classification: UserInstrumentClassificationDto;
	preference: CompositionColumnPreference;
}>;
const useReportComposition = (params: {
	columnsMetadata: ReportCompositionMetadata[];
	variant: ReportTemplateVariant;
	decimalPlaces: number;
}) => {
	const { t } = useReportTranslation();

	const rawColumns = useMemo(
		() =>
			params.columnsMetadata
				.filter((x) => !x.hidden)
				.flatMap((columnMetadata): (Omit<TableColumn<MixedInvestmentEntry>, "width"> & { width: number })[] => {
					switch (columnMetadata.id) {
						case "NAME":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>{columnMetadata.label}</TinyTableHeadCell>
									),
									content(row, props) {
										return <TinyTableDataCell {...props}>{row.name}</TinyTableDataCell>;
									},
									name: "Name",
									orderable: false,
									minWidth: 144,
									width: 144,
									maxWidth: undefined,
									headerCellStyle: { flexGrow: 1 },
									cellStyle: { flexGrow: 1 },
								},
							];
						case "DESCRIPTION":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>{columnMetadata.label}</TinyTableHeadCell>
									),
									content(row, props) {
										return (
											<div
												style={{
													...props.style,
													fontSize: props.style?.fontSize ?? tinyTableDataCellFontSize,
													minHeight: 31,
													overflow: "hidden",
													display: "-webkit-box",
													WebkitBoxOrient: "vertical",
													WebkitLineClamp: props.style?.lineClamp ?? 2,
												}}
												className={toClassName(props.className)}
											>
												<MarkdownRenderer title={row.description?.value ?? "Description"}>
													{row.description?.value}
												</MarkdownRenderer>
											</div>
										);
									},
									name: "Description",
									orderable: false,
									minWidth: 164,
									width: 164,
									maxWidth: undefined,
									headerCellStyle: { flexGrow: 1 },
									cellStyle: { flexGrow: 1 },
								},
							];
						case "WEIGHT":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>
											{t(`REPORT_BUILDER.COMPOSITION.TABLE.WEIGHT`)}
										</TinyTableHeadCell>
									),
									name: "weight",
									minWidth: undefined,
									width: 80,
									maxWidth: 80,
									orderable: false,
									cellClassName: "tabular-nums",
									content: ({ previousWeight, weight }, props) => (
										<TinyTableDataCell {...props}>{`${formatNumberWithBankersRounding(
											(params.variant === "proposal" ? previousWeight : weight) ?? 0,
											params.decimalPlaces,
										)}%`}</TinyTableDataCell>
									),
									align: "end",
								},
							];
						case "ENHANCED_WEIGHT":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>
											{t(`REPORT_BUILDER.COMPOSITION.TABLE.ENHANCED_WEIGHT`)}
										</TinyTableHeadCell>
									),
									name: "weight",
									minWidth: undefined,
									width: 60,
									maxWidth: 60,
									orderable: false,
									cellClassName: "tabular-nums",
									content: ({ weight }, props) => (
										<TinyTableDataCell {...props}>{`${formatNumberWithBankersRounding(
											weight ?? 0,
											params.decimalPlaces,
										)}%`}</TinyTableDataCell>
									),
									align: "end",
								},
							];
						case "DIFFERENCE":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>
											{t(`REPORT_BUILDER.COMPOSITION.TABLE.DIFFERENCE`)}
										</TinyTableHeadCell>
									),
									name: "difference",
									minWidth: undefined,
									width: 96,
									maxWidth: 96,
									orderable: false,
									align: "end",
									content: (instrument, props) => {
										const deltaWeight = bankersRound(
											(instrument.weight ?? 0) - (instrument.previousWeight ?? 0),
											params.decimalPlaces,
										);
										return (
											<TinyTableDataCell {...props}>
												<InfoDelta diff={deltaWeight} enh={instrument.weight ?? 0} />
											</TinyTableDataCell>
										);
									},
								},
							];
						case "ALIAS":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>
											{t(`REPORT_BUILDER.COMPOSITION.TABLE.ALIAS`)}
										</TinyTableHeadCell>
									),
									content(row, props) {
										return <TinyTableDataCell {...props}>{row.alias}</TinyTableDataCell>;
									},
									name: "alias",
									minWidth: undefined,
									width: 124,
									maxWidth: 124,
									orderable: false,
								},
							];
						case "ISIN":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>{t(`REPORT_BUILDER.COMPOSITION.TABLE.ISIN`)}</TinyTableHeadCell>
									),
									content(row, props) {
										return <TinyTableDataCell {...props}>{row.identifiers?.ISIN}</TinyTableDataCell>;
									},
									name: "identifiers.ISIN",
									minWidth: undefined,
									width: 124,
									maxWidth: 124,
									orderable: false,
								},
							];
						case "CUSIP":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>
											{t(`REPORT_BUILDER.COMPOSITION.TABLE.CUSIP`)}
										</TinyTableHeadCell>
									),
									content(row, props) {
										return <TinyTableDataCell {...props}>{row.identifiers?.CUSIP}</TinyTableDataCell>;
									},
									name: "identifiers.CUSIP",
									minWidth: undefined,
									width: 124,
									maxWidth: 124,
									orderable: false,
								},
							];
						case "BLOOMBERG_TICKER":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps}>
											{t(`REPORT_BUILDER.COMPOSITION.TABLE.BLOOMBERG_TICKER`)}
										</TinyTableHeadCell>
									),
									content(row, props) {
										return <TinyTableDataCell {...props}>{row.identifiers?.BLOOMBERG_TICKER}</TinyTableDataCell>;
									},
									name: "identifiers.BLOOMBERG_TICKER",
									minWidth: undefined,
									width: 124,
									maxWidth: 124,
									orderable: false,
								},
							];
						default:
							break;
					}

					if (!columnMetadata.meta.classification?.fieldType) {
						return [];
					}
					const fieldType = columnMetadata.meta.classification.fieldType;
					switch (fieldType) {
						case "QUALITY":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps} data-fieldType={fieldType}>
											{columnMetadata.label}
										</TinyTableHeadCell>
									),
									content(row, props) {
										return (
											<TinyTableDataCell {...props}>
												{row.qualities?.find((x) => x.classificationUuid === columnMetadata.id)?.value}
											</TinyTableDataCell>
										);
									},
									name: "quality",
									minWidth: undefined,
									width: 124,
									maxWidth: 124,
									orderable: false,
								},
							];
						case "SCORE":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps} data-fieldType={fieldType}>
											{columnMetadata.label}
										</TinyTableHeadCell>
									),
									content(row, props) {
										return (
											<TinyTableDataCell {...props}>
												{row.scores?.find((x) => x.classificationUuid === columnMetadata.id)?.value}
											</TinyTableDataCell>
										);
									},
									name: "score",
									minWidth: undefined,
									width: 96,
									maxWidth: 96,
									orderable: false,
								},
							];
						case "TAG":
							return [
								{
									header: (headerProps) => (
										<TinyTableHeadCell {...headerProps} data-fieldType={fieldType}>
											{columnMetadata.label}
										</TinyTableHeadCell>
									),
									content: (row, cellProps) => {
										const cellValue = row.tags?.find((x) => x.classificationUuid === columnMetadata.id);

										return (
											<TinyTableDataCell {...cellProps}>
												<TagCell classification={columnMetadata.meta.classification} values={cellValue?.values ?? []} />
											</TinyTableDataCell>
										);
									},
									name: columnMetadata.id,
									minWidth: 96,
									width: 96,
									maxWidth: undefined,
									orderable: false,
									headerCellStyle: { flexGrow: 1 },
									cellStyle: { flexGrow: 1 },
								},
							];
						default:
							return unreachable(fieldType);
					}
				}),
		[params.columnsMetadata, params.variant, params.decimalPlaces, t],
	);

	return rawColumns;
};

const Composition = ({
	config,
	variant,
	portfolio,
	userClassifications,
	list,
}: PrintableProps<
	{
		config: ReportTemplateItemMap["composition"];
		variant: ReportTemplateVariant;
		portfolio: InvestmentSummary;
		userClassifications: UserInstrumentClassificationDto[];
	},
	MixedInvestmentEntry
>): React.JSX.Element => {
	const { t } = useReportTranslation();
	const preferences = variant === "current" ? config.currentColumnPreferences : config.proposalColumnPreferences;
	const columnsMetadata = useMemo(() => {
		return preferences.map((preference): ReportCompositionMetadata => {
			const classification = userClassifications.find(
				(user) => user.classificationUuid === preference.preferenceType?.classificationUuid,
			);

			return {
				id: preference.preferenceType.classificationUuid,
				label: classification?.name ?? preference.preferenceType.label ?? "Untitled",
				meta: {
					classification: coalesce(classification, { dataVisualization: "TAG_VIEW_WITHOUT_COLOR" }),
					preference,
				},
				hidden: !preference.enabled,
			};
		});
	}, [preferences, userClassifications]);

	const columns = useReportComposition({ columnsMetadata, variant, decimalPlaces: config.decimalPlaces ?? 2 });
	return (
		<Card title={t("REPORT_BUILDER.COMPOSITION.TITLE")}>
			<BaseTable
				disableVirtualScroll
				palette="uniform"
				columns={columns}
				rows={list}
				noDataText={t("COMPOSITION.NO_COMPOSITION")}
			/>
		</Card>
	);
};

export default Composition;
